import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Divider, Row } from 'antd';
import { camelCase, cloneDeep } from 'es-toolkit';

import { generateUUIDv4 } from '@/utils/number';
import { ColumnOptions } from '@/views/components/DragAndDrop/ColumnOptions';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';

import Button from '../../../../components/Buttons/Button';
import { OC_FIXED_COLUMN_LABELS } from '../data-provider/table-view-columns/normalize';
import { useGetOCSortingFields, useGetOCTableViewColumns, useSaveOCTableViewColumns } from '../hooks';

const OCClaimTableViewFieldsDrawer = ({ onClose }) => {
  const [selectedColumns, updateSelectedColumns] = useState([]);
  const { data: sortingFields = [] } = useGetOCSortingFields();
  const { data: tableViewColumns = [], isFetching: isTableViewColumnsLoading } = useGetOCTableViewColumns();
  const { mutate: saveTableViewColumns, isPending: saveTableViewColumnsProgressing } = useSaveOCTableViewColumns({
    onSuccess: onClose,
  });

  useEffect(() => {
    updateSelectedColumns(tableViewColumns);
  }, [tableViewColumns]);

  const availableColumns = useMemo(() => {
    const data = sortingFields.map(({ value, key }) => ({
      label: value,
      value: camelCase(key),
    }));
    return data.filter(({ label }) => !OC_FIXED_COLUMN_LABELS.includes(label));
  }, [sortingFields]);

  const selectedColumnValuesArray = useMemo(
    () => selectedColumns.map(({ columnValue }) => columnValue),
    [selectedColumns],
  );

  const handleOnDragEnd = (result) => {
    if (!result) {
      return;
    }
    updateSelectedColumns(result);
  };

  const handleAddNewColumn = () => {
    const selectedColumnsLocal = cloneDeep(selectedColumns);
    selectedColumnsLocal.push({
      columnName: undefined,
      columnValue: undefined,
      id: generateUUIDv4(),
    });
    updateSelectedColumns(selectedColumnsLocal);
  };

  const handleRemoveColumn = (index) => {
    const selectedColumnsLocal = cloneDeep(selectedColumns);
    selectedColumnsLocal.splice(index, 1);
    updateSelectedColumns(selectedColumnsLocal);
  };

  const handleSelectValueChange = useCallback(
    (val, index) => {
      const selectedColumnsLocal = cloneDeep(selectedColumns);
      selectedColumnsLocal[index].columnName = val.label;
      selectedColumnsLocal[index].columnValue = val.value;
      updateSelectedColumns(selectedColumnsLocal);
    },
    [selectedColumns],
  );

  const hasValidationErrors = (columns) => {
    return columns.some((col) => !col.columnName || !col.columnValue);
  };

  const handleSaveColumns = () => {
    if (!selectedColumns?.length) {
      showNotification({
        message: 'Please select at least one column',
        type: NOTIFICATION_TYPES.ERROR,
      });
      return;
    }
    if (hasValidationErrors(selectedColumns)) {
      showNotification({
        message: 'Cannot add a column without selecting a column name',
        type: NOTIFICATION_TYPES.ERROR,
      });
      return;
    }
    saveTableViewColumns({
      columnOptions: selectedColumns.map(({ columnName, columnValue }) => ({
        fieldReferenceName: columnName,
        fieldReferenceValue: columnValue,
      })),
      gridName: 'OverChargeClaims',
    });
  };

  return (
    <Row className="h-100 overflow-hidden flex-direction-column">
      <Col className="flex-1 py-15 overflow-hidden">
        <div className="px-15 h-100 custom-scroll-bar">
          <ColumnOptions
            availableColumns={availableColumns}
            isColumnsLoading={isTableViewColumnsLoading}
            handleOnDragEnd={handleOnDragEnd}
            selectedColumns={selectedColumns}
            handleAddNewColumn={handleAddNewColumn}
            handleRemoveColumn={handleRemoveColumn}
            handleSelectValueChange={handleSelectValueChange}
            selectedColumnValuesArray={selectedColumnValuesArray}
          />
        </div>
      </Col>
      <Col>
        <Divider className="my-15 border-bottom-border" />
      </Col>
      <Col className="px-15 pb-15">
        <Row justify="end" gutter={15}>
          <Col>
            <Button variant="danger" type="default" onClick={() => onClose()}>
              Cancel
            </Button>
          </Col>
          <Col>
            <Button onClick={handleSaveColumns} loading={saveTableViewColumnsProgressing}>
              Save
            </Button>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default OCClaimTableViewFieldsDrawer;
